import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { NotificationManager } from 'react-notifications';
import { addToListDemographics } from '../../../../services/discovery.service';
import { addNewList } from '../../../../services/list.service';
import { changeRefetchStatus } from '../../../../redux/relationship/relationshipActions';
import { evictAndFetchList } from '../../../../redux/lists/listActions';
import { Popover } from '@mui/material';
import DoneIcon from '@mui/icons-material/DoneRounded';
import '../../components/selects/_selects.scss';
import '../demographics.scss';
import { createListSchema } from '../../../../validations/list';

export default function AddToList({
  anchorEl,
  setAnchorEl,
  selectedInfluencer,
  demographicsId,
  darkMode = false,
  ///////
  discoveryData,
  setDiscoveryData,
  selectedIds,
}) {
  const [selectedListId, setSelectedListId] = useState('');
  const [isCreatingList, setIsCreatingList] = useState(false);
  const [listName, setListName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [errors, setErrors] = useState({});
  const { allLists } = useSelector((state) => state.list);
  const dispatch = useDispatch();

  const [sortedLists, setSortedLists] = useState([]);

  useEffect(() => {
    const sorted = allLists?.sort((a, b) => {
      if (a.lastAddedByDiscoveryAt === null && b.lastAddedByDiscoveryAt === null) {
        return 0;
      } else if (a.lastAddedByDiscoveryAt === null) {
        return 1;
      } else if (b.lastAddedByDiscoveryAt === null) {
        return -1;
      } else {
        return new Date(b.lastAddedByDiscoveryAt).getTime() - new Date(a.lastAddedByDiscoveryAt).getTime();
      }
    });
    setSortedLists(sorted);
  }, [allLists]);

  const handleAddToList = async (listId) => {
    const body = {
      demographicId: demographicsId,
      ...selectedInfluencer,
      listId: typeof listId === 'string' ? listId : selectedListId,
    };
    let updatedAccountsData;
    updatedAccountsData = discoveryData?.map((data) => {
      if (selectedIds.includes(data.account.user_profile.user_id)) {
        return { ...data, isAlreadyAdded: true };
      } else {
        return { ...data };
      }
    });

    const response = await addToListDemographics(body);
    if (response?.status) {
      NotificationManager.success(response?.message, '', 1200);
      setAnchorEl(null);
      if (setDiscoveryData) {
        setDiscoveryData(updatedAccountsData);
      }
      dispatch(changeRefetchStatus(true));
      dispatch(evictAndFetchList());
    } else {
      setErrorMessage(response?.message);
      NotificationManager.error(response?.message || 'Error adding influencer to list.', '', 1500);
    }
  };

  const validateData = async (body) => {
    const { error } = createListSchema.validate(body, { abortEarly: false });

    if (error) {
      const validationErrors = {};
      error.details.forEach((detail) => {
        validationErrors[detail.context.key] = detail.message;
      });

      setErrors(validationErrors);
      return false;
    } else {
      return true;
    }
  };

  const handleCreateAndAdd = async () => {
    setErrorMessage(false);
    let body = { name: listName, listCreatedType: 'demographics' };
    const isValid = await validateData(body);
    if (!isValid) {
      return;
    }
    const response = await addNewList(body);
    if (response?.status) {
      setListName('');
      handleAddToList(response.data._id);
      dispatch(evictAndFetchList());
    } else {
      setErrorMessage(response?.message || 'Something went wrong!');
    }
  };

  return (
    <>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setSelectedListId('');
          setListName('');
          setIsCreatingList(false);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ style: { backgroundColor: 'transparent', boxShadow: 'none', borderRadius: 0, zIndex: 99999 } }}>
        <div className={darkMode ? 'drop-down-container-dark' : 'drop-down-container'} style={{ width: 350 }}>
          <div className="dropdown1-title" style={{ justifyContent: 'space-between', padding: '8px 12px 8px 12px' }}>
            <p style={{ fontSize: 14 }}>{isCreatingList ? 'Create' : 'Select'} a list to save the creators</p>
          </div>
          {!isCreatingList && (
            <div className="dm-add-to-list-container">
              {allLists && allLists?.length > 0 ? (
                sortedLists?.map((list) => (
                  <p
                    className="dm-add-to-list-item"
                    key={list._id}
                    onClick={() => setSelectedListId(list._id)}
                    style={{ color: selectedListId === list._id ? '#7357E9' : darkMode ? '#fff' : '#353535' }}>
                    {list.name}
                    {selectedListId === list._id && <DoneIcon />}
                  </p>
                ))
              ) : (
                <div style={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center' }}>
                  <p style={{ color: darkMode ? '#fff' : '#808080' }}>No Lists Created!</p>
                </div>
              )}
            </div>
          )}
          {isCreatingList && (
            <div className="list-input-container">
              <label className="required-field">List Name</label>
              <input
                type="text"
                placeholder="Enter list name..."
                autoFocus
                value={listName}
                onChange={(e) => setListName(e.target.value)}
              />
              {errors?.name && <p className="error-message">{errors?.name}</p>}
              {errorMessage && <p className="error-message">{errorMessage}</p>}
            </div>
          )}
          <div className="dropdown1-buttons-container">
            {!isCreatingList && (
              <>
                <button className="create-new-list-btn" onClick={() => setIsCreatingList(true)}>
                  Create New List
                </button>
                <button className="add-to-list-btn" disabled={!selectedListId} onClick={handleAddToList}>
                  Add to List
                </button>
              </>
            )}
            {isCreatingList && (
              <>
                <button onClick={() => setIsCreatingList(false)} className="cancel-btn">
                  Cancel
                </button>
                <button className="add-to-list-btn" disabled={!listName} onClick={handleCreateAndAdd}>
                  Create & Add
                </button>
              </>
            )}
          </div>
        </div>
      </Popover>
    </>
  );
}
